import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Monitor, Smartphone, ExternalLink, RefreshCw, Check, Loader2 } from 'lucide-react';
import { siteApi, previewUrl } from './api.ts';
import { useSiteQuery } from './hooks.ts';
import type { SaveStatus } from './useAutosave.ts';

// Live preview of the council's real, draft-inclusive rendered site. Points at
// the token-gated /api/site-preview/render endpoint for the current `path`, and
// reloads whenever the site bundle changes (i.e. after any autosave).
export function PreviewPane({ path, saveStatus }: { path: string; saveStatus?: SaveStatus }): JSX.Element {
  const [device, setDevice] = useState<'desktop' | 'mobile'>('desktop');
  const [lang, setLang] = useState<'dv' | 'en'>('dv');
  const [refreshKey, setRefreshKey] = useState(1);

  const token = useQuery({
    queryKey: ['preview-token'],
    queryFn: siteApi.previewToken,
    staleTime: 10 * 60 * 1000,
    refetchInterval: 10 * 60 * 1000,
    retry: false,
  });

  // Reload the frame after any save (the site query is invalidated on success).
  const { dataUpdatedAt } = useSiteQuery();
  const firstUpdate = useRef(dataUpdatedAt);
  useEffect(() => {
    if (dataUpdatedAt && dataUpdatedAt !== firstUpdate.current) {
      firstUpdate.current = dataUpdatedAt;
      const t = setTimeout(() => setRefreshKey((k) => k + 1), 350);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [dataUpdatedAt]);

  const src = useMemo(
    () => (token.data ? previewUrl(token.data, lang, path, refreshKey) : ''),
    [token.data, lang, path, refreshKey],
  );

  return (
    <div className="flex h-full flex-col">
      {/* toolbar */}
      <div className="flex items-center gap-2 border-b border-border px-3 py-2">
        <div className="inline-flex rounded-lg border border-border p-0.5">
          <button
            type="button" aria-label="Desktop preview" aria-pressed={device === 'desktop'}
            onClick={() => setDevice('desktop')}
            className={`inline-flex items-center justify-center rounded-md px-2 py-1 ${device === 'desktop' ? 'bg-primary/10 text-primary' : 'text-muted-foreground'}`}
          ><Monitor size={15} /></button>
          <button
            type="button" aria-label="Mobile preview" aria-pressed={device === 'mobile'}
            onClick={() => setDevice('mobile')}
            className={`inline-flex items-center justify-center rounded-md px-2 py-1 ${device === 'mobile' ? 'bg-primary/10 text-primary' : 'text-muted-foreground'}`}
          ><Smartphone size={15} /></button>
        </div>

        <div className="inline-flex rounded-lg border border-border p-0.5 text-xs font-medium">
          <button type="button" onClick={() => setLang('dv')} className={`rounded-md px-2 py-1 ${lang === 'dv' ? 'bg-primary/10 text-primary' : 'text-muted-foreground'}`} lang="dv" dir="rtl">ދިވެހި</button>
          <button type="button" onClick={() => setLang('en')} className={`rounded-md px-2 py-1 ${lang === 'en' ? 'bg-primary/10 text-primary' : 'text-muted-foreground'}`}>EN</button>
        </div>

        <div className="ms-auto flex items-center gap-2">
          <SaveIndicator status={saveStatus} />
          <button type="button" aria-label="Refresh preview" onClick={() => setRefreshKey((k) => k + 1)} className="text-muted-foreground hover:text-foreground p-1">
            <RefreshCw size={15} />
          </button>
          {src && (
            <a href={src} target="_blank" rel="noopener noreferrer" aria-label="Open preview in new tab" className="text-muted-foreground hover:text-foreground p-1">
              <ExternalLink size={15} />
            </a>
          )}
        </div>
      </div>

      {/* frame */}
      <div className="flex-1 overflow-auto bg-muted/40 p-3">
        <div
          className="mx-auto h-full bg-white shadow-sm transition-[max-width] duration-300 rounded-lg overflow-hidden ring-1 ring-border"
          style={{ maxWidth: device === 'mobile' ? 390 : '100%' }}
        >
          {token.isError ? (
            <div className="grid h-full place-items-center p-6 text-center text-sm text-muted-foreground">
              Couldn't load the preview. Please refresh the page.
            </div>
          ) : src ? (
            <iframe
              key={src}
              src={src}
              title="Live site preview"
              className="h-full w-full border-0"
              style={{ minHeight: '60vh' }}
            />
          ) : (
            <div className="grid h-full place-items-center p-6 text-sm text-muted-foreground">Loading preview…</div>
          )}
        </div>
      </div>
    </div>
  );
}

function SaveIndicator({ status }: { status?: SaveStatus }): JSX.Element | null {
  if (!status || status === 'idle') return null;
  if (status === 'saving') return <span className="inline-flex items-center gap-1 text-xs text-muted-foreground"><Loader2 size={13} className="animate-spin" /> Saving…</span>;
  if (status === 'saved') return <span className="inline-flex items-center gap-1 text-xs text-[hsl(var(--success))]"><Check size={13} /> Saved</span>;
  return <span className="text-xs text-destructive">Couldn't save</span>;
}
